import { useState, useEffect, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { TableBody as AriaTableBody } from 'react-aria-components'
import axios from 'axios'
import { PlusIcon, Edit01Icon, Trash01Icon } from '@untitledui-icons/react/line'
import { Play, Square } from 'lucide-react'

import { Table, TableCard } from '@/components/application/table/table'
import { Button } from '@/components/base/buttons'
import { Badge } from '@/components/ui/Badge'
import { Input } from '@/components/ui/Input'
import { Switch } from '@/components/ui/Switch'
import { LoadingSpinner, EmptyState, ScoreBar, FilterSelect } from '@/components/common'
import { AlertDialog } from '@/components/ui/AlertDialog'
import {
  useAdminVoices,
  useToggleVoiceFlag,
  useToggleVoiceSuggested,
  useDeleteVoice,
  type VoiceFilters,
} from '@/hooks/api'
import { useAudioPreview } from '@/hooks/useAudioPreview'
import { GENDER_OPTIONS, AGE_GROUP_OPTIONS } from '@/lib/voiceConstants'
import { getProviderLabel } from '@/lib/voiceDisplay'
import { getLanguageFlag, getLanguageDisplayName } from '@/lib/languageFlags'
import { toast } from '@/lib/toast'
import type { Voice } from '@/types/assistant'
import type { VoiceToggleField } from '@/types/voiceAdmin'
import { VoiceFormDrawer } from './VoiceFormDrawer'

/** Debounce delay for the search input, in milliseconds. */
const SEARCH_DEBOUNCE_MS = 300

/**
 * Returns `value` after it has been stable for `delayMs`. Keeps the search
 * input itself immediately responsive while the query filter lags behind.
 */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value)

  useEffect(() => {
    const timeout = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(timeout)
  }, [value, delayMs])

  return debounced
}

export function VoiceAdminPage() {
  const { t, i18n } = useTranslation()

  const [search, setSearch] = useState('')
  const [genderFilter, setGenderFilter] = useState<string | null>(null)
  const [ageFilter, setAgeFilter] = useState<string | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editingVoice, setEditingVoice] = useState<Voice | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Voice | null>(null)

  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS)

  // The admin list reuses the (server-side admin-gated) voices endpoint.
  const filters: VoiceFilters = useMemo(
    () => ({
      q: debouncedSearch || undefined,
      gender: genderFilter || undefined,
      age_group: ageFilter || undefined,
    }),
    [debouncedSearch, genderFilter, ageFilter]
  )

  // With keepPreviousData, isLoading is only true on the very first load;
  // filter/search refetches keep the previous list on screen.
  const { data: voices = [], isLoading, isFetching, isError, refetch } = useAdminVoices(filters)
  const toggleFlag = useToggleVoiceFlag()
  const toggleSuggested = useToggleVoiceSuggested()
  const deleteVoice = useDeleteVoice()

  // Inline audio preview for the list rows (shared state machine with the
  // form drawer and the customer voice picker).
  const {
    playingId: previewPlayingId,
    loadingId: previewLoadingId,
    toggle: togglePreview,
  } = useAudioPreview({
    errorMessage: t('resources.admin.voices.errors.preview_failed'),
  })

  const handlePreview = useCallback(
    (voice: Voice) => {
      // No `text`: the backend picks a localized sample greeting for the language.
      void togglePreview(voice.id, {
        language: voice.meta?.native_languages?.[0] || voice.meta?.languages?.[0] || 'en-US',
        id: 0,
        voice_id: voice.id,
      })
    },
    [togglePreview]
  )

  // Result-state logic (empty-state choice, clear-filters affordance) is based
  // on the *debounced* search the data is actually keyed on, so the labels
  // never contradict the list during the debounce window. Only the input
  // itself reflects the immediate `search` state.
  const hasActiveFilters = debouncedSearch !== '' || genderFilter !== null || ageFilter !== null

  const clearFilters = useCallback(() => {
    setSearch('')
    setGenderFilter(null)
    setAgeFilter(null)
  }, [])

  // Canonical facet options, so the filters never disappear with the results.
  const genderOptions = useMemo(
    () => GENDER_OPTIONS.map((opt) => ({ value: opt.value, label: t(opt.labelKey) })),
    [t]
  )
  const ageGroupOptions = useMemo(
    () => AGE_GROUP_OPTIONS.map((opt) => ({ value: opt.value, label: t(opt.labelKey) })),
    [t]
  )

  // Pending toggles tracked per `${id}:${field}` key in local state. A single
  // mutation observer only exposes the *latest* call's `variables`/`isPending`,
  // so deriving per-row pending state from it breaks as soon as two toggles
  // overlap (the first row would re-enable while its PATCH is still in flight).
  const [pendingToggles, setPendingToggles] = useState<ReadonlySet<string>>(new Set())

  // Wraps a toggle mutation with per-key pending tracking and the shared
  // failure toast; `conflictMessage` names the state the toggle collided with.
  const runToggle = useCallback(
    async (key: string, mutate: () => Promise<unknown>, conflictMessage: string) => {
      setPendingToggles((prev) => new Set(prev).add(key))
      try {
        await mutate()
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 409) {
          toast.error(conflictMessage)
        } else {
          toast.error(t('resources.admin.voices.errors.toggle_failed'))
        }
      } finally {
        setPendingToggles((prev) => {
          const next = new Set(prev)
          next.delete(key)
          return next
        })
      }
    },
    [t]
  )

  const handleToggle = useCallback(
    (voice: Voice, field: VoiceToggleField, value: boolean) =>
      runToggle(
        `${voice.id}:${field}`,
        () => toggleFlag.mutateAsync({ id: voice.id, field, value }),
        // Deactivating the default voice or a language default is refused.
        field === 'is_active' && !value
          ? t('resources.admin.voices.errors.deactivate_conflict')
          : t('resources.admin.voices.errors.conflict')
      ),
    [runToggle, toggleFlag, t]
  )

  const handleToggleSuggested = useCallback(
    (voice: Voice, value: boolean) =>
      runToggle(
        `${voice.id}:suggested`,
        () => toggleSuggested.mutateAsync({ voice, value }),
        t('resources.admin.voices.errors.conflict')
      ),
    [runToggle, toggleSuggested, t]
  )

  const isTogglePending = useCallback(
    (voice: Voice, field: VoiceToggleField | 'suggested') =>
      pendingToggles.has(`${voice.id}:${field}`),
    [pendingToggles]
  )

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return
    try {
      await deleteVoice.mutateAsync(deleteTarget.id)
      toast.success(t('resources.admin.voices.deleted'))
      setDeleteTarget(null)
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        toast.error(t('resources.admin.voices.errors.delete_conflict'))
      } else {
        toast.error(t('resources.admin.voices.errors.delete_failed'))
      }
      // Re-throw so the AlertDialog stays open for a retry.
      throw error
    }
  }, [deleteTarget, deleteVoice, t])

  const openCreate = useCallback(() => {
    setEditingVoice(null)
    setDrawerOpen(true)
  }, [])

  const openEdit = useCallback((voice: Voice) => {
    setEditingVoice(voice)
    setDrawerOpen(true)
  }, [])

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col gap-6 p-6">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            {t('resources.admin.voices.title')}
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t('resources.admin.voices.subtitle')}
          </p>
        </div>
        <Button color="primary" size="md" iconLeading={PlusIcon} onPress={openCreate}>
          {t('resources.admin.voices.create')}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="w-full max-w-xs">
          <Input
            type="search"
            aria-label={t('resources.admin.voices.search_placeholder')}
            placeholder={t('resources.admin.voices.search_placeholder')}
            value={search}
            onChange={setSearch}
          />
        </div>
        <FilterSelect
          label={t('resources.admin.voices.fields.gender')}
          value={genderFilter}
          options={genderOptions}
          onChange={setGenderFilter}
          anyLabel={t('resources.admin.voices.filters.all')}
        />
        <FilterSelect
          label={t('resources.admin.voices.fields.age_group')}
          value={ageFilter}
          options={ageGroupOptions}
          onChange={setAgeFilter}
          anyLabel={t('resources.admin.voices.filters.all')}
        />
        {hasActiveFilters && (
          <Button color="tertiary" size="sm" onPress={clearFilters}>
            {t('resources.admin.voices.filters.clear')}
          </Button>
        )}
      </div>

      {/* Table */}
      {isError ? (
        <div className="flex flex-1 items-center justify-center">
          <EmptyState
            iconVariant="error"
            title={t('resources.admin.voices.errors.load_failed')}
            action={{
              label: t('components.voice_call.retry'),
              onClick: () => void refetch(),
              variant: 'secondary',
            }}
          />
        </div>
      ) : voices.length === 0 && !isFetching ? (
        <div className="flex flex-1 items-center justify-center">
          {hasActiveFilters ? (
            <EmptyState
              title={t('resources.admin.voices.no_results')}
              description={t('resources.admin.voices.no_results_description')}
              action={{
                label: t('resources.admin.voices.filters.clear'),
                onClick: clearFilters,
                variant: 'secondary',
              }}
            />
          ) : (
            <EmptyState
              title={t('resources.admin.voices.empty')}
              description={t('resources.admin.voices.empty_description')}
              action={{
                label: t('resources.admin.voices.create'),
                onClick: openCreate,
              }}
            />
          )}
        </div>
      ) : (
        <TableCard.Root size="sm" className="flex flex-1 flex-col">
          <TableCard.Header title={t('resources.admin.voices.overview')} />

          {isFetching && (
            <div className="h-1 w-full overflow-hidden bg-gray-100">
              <div className="bg-brand-500 h-full w-1/3 animate-pulse" />
            </div>
          )}

          <div className="flex-1 overflow-auto">
            <Table aria-label={t('resources.admin.voices.overview')}>
              <Table.Header>
                <Table.Head
                  id="name"
                  label={t('resources.admin.voices.fields.display_name')}
                  isRowHeader
                />
                <Table.Head id="provider" label={t('resources.admin.voices.fields.provider')} />
                <Table.Head
                  id="languages"
                  label={t('resources.admin.voices.fields.languages')}
                  className="hidden md:table-cell"
                />
                <Table.Head
                  id="scores"
                  label={t('resources.admin.voices.columns.scores')}
                  className="hidden md:table-cell"
                />
                <Table.Head
                  id="assistants"
                  label={t('resources.admin.voices.columns.assistants')}
                  className="hidden md:table-cell"
                />
                <Table.Head
                  id="recommended"
                  label={t('resources.admin.voices.fields.recommended')}
                  className="hidden md:table-cell"
                />
                <Table.Head id="public" label={t('resources.admin.voices.fields.is_public')} />
                <Table.Head id="active" label={t('resources.admin.voices.fields.is_active')} />
                <Table.Head id="actions" label="" />
              </Table.Header>

              <AriaTableBody>
                {voices.map((voice) => {
                  const langs = voice.meta?.languages ?? []
                  const flags = langs.slice(0, 3)
                  const extra = Math.max(0, langs.length - flags.length)
                  const langNames = langs
                    .map((lang) => getLanguageDisplayName(lang, i18n.language) ?? lang)
                    .join(', ')
                  const providerLabel = getProviderLabel(voice)
                  const defaultFor = voice.meta?.default_for ?? []
                  const defaultForNames = defaultFor
                    .map((lang) => getLanguageDisplayName(lang, i18n.language) ?? lang)
                    .join(', ')
                  const deleteBlockedReason = voice.is_default
                    ? t('resources.admin.voices.delete_blocked_default')
                    : defaultFor.length > 0
                      ? t('resources.admin.voices.delete_blocked_language_default')
                      : voice.in_use
                        ? t('resources.admin.voices.delete_blocked_in_use')
                        : undefined
                  // Reasons and names live in sr-only text referenced via
                  // aria-describedby: a `title` alone is mouse-only, and react-aria
                  // keeps aria-describedby on disabled buttons.
                  const deleteReasonId = `voice-${voice.id}-delete-reason`
                  // A language default voice must stay public (the backend answers 400
                  // on the toggle), so block the switch with the same sr-only reason
                  // pattern as delete instead of surfacing a generic failure toast.
                  const publicBlockedReason =
                    voice.is_public && defaultFor.length > 0
                      ? t('resources.admin.voices.public_blocked_language_default')
                      : undefined
                  const publicReasonId = `voice-${voice.id}-public-reason`
                  const defaultForNamesId = `voice-${voice.id}-default-for`
                  const activeHelpId = `voice-${voice.id}-active-help`
                  const activeHelp = t('resources.admin.voices.helpers.is_active_toggle', {
                    count: voice.assistant_count ?? 0,
                  })
                  return (
                    <Table.Row key={voice.id} id={voice.id}>
                      <Table.Cell>
                        <span className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {voice.display_name}
                          </span>
                          {voice.is_default && (
                            <Badge variant="brand" size="sm">
                              {t('resources.admin.voices.fields.is_default')}
                            </Badge>
                          )}
                          {defaultFor.length > 0 && (
                            <span title={defaultForNames}>
                              <Badge
                                variant="success"
                                size="sm"
                                aria-describedby={defaultForNamesId}
                              >
                                {t('resources.admin.voices.default_for_badge', {
                                  count: defaultFor.length,
                                })}
                              </Badge>
                              <span id={defaultForNamesId} className="sr-only">
                                {defaultForNames}
                              </span>
                            </span>
                          )}
                          {voice.is_active === false && (
                            <Badge variant="gray" size="sm">
                              {t('resources.admin.voices.inactive')}
                            </Badge>
                          )}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        {providerLabel && (
                          <Badge variant="gray" size="sm">
                            {providerLabel}
                          </Badge>
                        )}
                      </Table.Cell>
                      <Table.Cell className="hidden md:table-cell">
                        {flags.length > 0 ? (
                          <span
                            className="flex items-center gap-0.5 text-sm leading-none"
                            title={langNames}
                          >
                            {flags.map((lang) => (
                              <span key={lang} aria-hidden="true">
                                {getLanguageFlag(lang)}
                              </span>
                            ))}
                            {extra > 0 && (
                              <span aria-hidden="true" className="ml-0.5 text-xs text-gray-400">
                                +{extra}
                              </span>
                            )}
                            <span className="sr-only">{langNames}</span>
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </Table.Cell>
                      <Table.Cell className="hidden md:table-cell">
                        <div className="flex flex-col gap-1.5">
                          <div className="flex items-center gap-2">
                            <span className="w-16 text-[11px] text-gray-400">
                              {t('resources.admin.voices.fields.naturalness')}
                            </span>
                            <ScoreBar
                              score={voice.meta?.scores?.naturalness}
                              label={t('resources.admin.voices.fields.naturalness')}
                              notScoredLabel={t('resources.admin.voices.not_scored')}
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="w-16 text-[11px] text-gray-400">
                              {t('resources.admin.voices.fields.speed')}
                            </span>
                            <ScoreBar
                              score={voice.meta?.scores?.speed}
                              label={t('resources.admin.voices.fields.speed')}
                              notScoredLabel={t('resources.admin.voices.not_scored')}
                            />
                          </div>
                        </div>
                      </Table.Cell>
                      <Table.Cell className="hidden md:table-cell">
                        {(voice.assistant_count ?? 0) > 0 ? (
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            {voice.assistant_count}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">–</span>
                        )}
                      </Table.Cell>
                      <Table.Cell className="hidden md:table-cell">
                        <div className="flex items-center gap-2">
                          <Switch
                            size="sm"
                            aria-label={t('resources.admin.voices.aria.toggle_recommended', {
                              name: voice.display_name,
                            })}
                            isSelected={voice.meta?.suggested === true}
                            isDisabled={isTogglePending(voice, 'suggested')}
                            onChange={(v) => handleToggleSuggested(voice, v)}
                          />
                          {voice.meta?.suggested === true && (
                            <span className="text-xs text-gray-400">
                              · {voice.meta?.suggested_weight ?? 0}
                            </span>
                          )}
                        </div>
                      </Table.Cell>
                      <Table.Cell>
                        <Switch
                          size="sm"
                          aria-label={t('resources.admin.voices.aria.toggle_public', {
                            name: voice.display_name,
                          })}
                          aria-describedby={publicBlockedReason ? publicReasonId : undefined}
                          isSelected={!!voice.is_public}
                          isDisabled={isTogglePending(voice, 'is_public') || !!publicBlockedReason}
                          onChange={(v) => handleToggle(voice, 'is_public', v)}
                        />
                        {publicBlockedReason && (
                          <span id={publicReasonId} className="sr-only">
                            {publicBlockedReason}
                          </span>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        <span title={activeHelp}>
                          <Switch
                            size="sm"
                            aria-label={t('resources.admin.voices.aria.toggle_active', {
                              name: voice.display_name,
                            })}
                            aria-describedby={activeHelpId}
                            isSelected={voice.is_active !== false}
                            isDisabled={isTogglePending(voice, 'is_active')}
                            onChange={(v) => handleToggle(voice, 'is_active', v)}
                          />
                          <span id={activeHelpId} className="sr-only">
                            {activeHelp}
                          </span>
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <div className="flex items-center gap-1">
                          <Button
                            color="tertiary"
                            size="sm"
                            iconLeading={previewPlayingId === voice.id ? Square : Play}
                            isLoading={previewLoadingId === voice.id}
                            onPress={() => handlePreview(voice)}
                            aria-label={t('resources.admin.voices.aria.play_preview', {
                              name: voice.display_name,
                            })}
                          />
                          <Button
                            color="tertiary"
                            size="sm"
                            iconLeading={Edit01Icon}
                            onPress={() => openEdit(voice)}
                            aria-label={t('resources.admin.voices.aria.edit', {
                              name: voice.display_name,
                            })}
                          />
                          {/* react-aria disabled buttons swallow pointer events,
                              so the tooltip explaining why lives on a wrapper. */}
                          <span title={deleteBlockedReason}>
                            <Button
                              color="tertiary-destructive"
                              size="sm"
                              iconLeading={Trash01Icon}
                              isDisabled={!!deleteBlockedReason}
                              onPress={() => setDeleteTarget(voice)}
                              aria-label={t('resources.admin.voices.aria.delete', {
                                name: voice.display_name,
                              })}
                              aria-describedby={deleteBlockedReason ? deleteReasonId : undefined}
                            />
                            {deleteBlockedReason && (
                              <span id={deleteReasonId} className="sr-only">
                                {deleteBlockedReason}
                              </span>
                            )}
                          </span>
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  )
                })}
              </AriaTableBody>
            </Table>
          </div>
        </TableCard.Root>
      )}

      {/* Create / edit drawer */}
      <VoiceFormDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        voice={editingVoice}
      />

      {/* Delete confirmation */}
      <AlertDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        variant="error"
        title={t('resources.admin.voices.delete_title')}
        description={t('resources.admin.voices.delete_description', {
          name: deleteTarget?.display_name,
        })}
        confirmLabel={t('resources.admin.voices.delete_confirm')}
        cancelLabel={t('components.confirm_dialog.cancel')}
        loadingLabel={t('resources.generic.loading')}
        isLoading={deleteVoice.isPending}
        closeOnConfirmError={false}
        blockDismissWhileBusy
      />
    </div>
  )
}

export default VoiceAdminPage
